// SaverLife preset for hero_motion.js. One member-app phone walks through six
// screens from the Figma "SL animation" frames (1301x1080 each), in order:
//
//   1. Dashboard. Three question chips grow out of the phone's "Ask anything
//      about your money" field; the third one gets pressed and glides back in.
//   2. The Financial Navigator chat, with check-in bubbles and the Preferences
//      card flying in from the left.
//   3 -> 4. Dashboard again; the screen content scrolls down to "Your next
//      steps" while the top bar, ask field and bottom nav stay fixed.
//   5. The phone shifts left and the "What is your #1 financial goal?" menu
//      comes in; "Increase my income" gets pressed.
//   6. The dashboard for the new goal.
//
// Layer art: src/images/projects/saverlife/motion/ (2x WebP exports).
const STAGE_W = 1301
const STAGE_H = 1080

// Phone frame and the dashboard panel inside it (stage units).
const PHONE = { w: 407, h: 838 }
const SCREEN = { x: 16, y: 72, w: 375, h: 696 }
const ASK_BANNER = { x: 16, y: 677, w: 375, h: 91 }
const SCROLL = 403 // how far screen 3 scrolls to become screen 4
const STRIP_H = 1099

// Scene 5 goal menu: option rows are 38 tall on a 52 pitch, starting at y 113.
const GOAL_ROW = { x: 16, y: 113 + 52, w: 329, h: 38 } // "Increase my income"

const OFF = 1150 // fly-in distance for floating cards

function build({ base, reduce, rnd, gsap, addLayer }) {
  const phone = (src, x, y, className) => addLayer({ src, x, y, w: PHONE.w, h: PHONE.h, central: true, className })
  const card = (src, x, y, w, h) => addLayer({ src, x, y, w, h })

  // A soft tint over a tappable area, revealed briefly to read as a press.
  function pressOverlay(layer, rect) {
    const el = document.createElement("div")
    el.className = "hm-press"
    el.style.left = rect.x + "px"
    el.style.top = rect.y + "px"
    el.style.width = rect.w + "px"
    el.style.height = rect.h + "px"
    layer._float.appendChild(el)
    return el
  }

  // Scene 1
  const phone1 = phone("phone-1.webp", 244, 135)
  const chipA = card("chip-a.webp", 740, 409, 404, 52)
  const chipB = card("chip-b.webp", 740, 515, 404, 80)
  const chipC = card("chip-c.webp", 740, 651, 404, 52)
  const chipCPress = pressOverlay(chipC, { x: 0, y: 0, w: 404, h: 52 })

  if (reduce) return null

  // The ask field inside phone 1, in stage units: phone (244,135) + field (32,716), 343 wide.
  const ASK_FIELD = { x: 244 + 32, y: 135 + 716, w: 343 }
  const chips = [chipA, chipB, chipC]
  // Where each chip sits when tucked into the ask field (top-left anchored).
  const tucked = (chip) => ({
    x: ASK_FIELD.x - parseFloat(chip.style.left),
    y: ASK_FIELD.y - parseFloat(chip.style.top),
    scale: ASK_FIELD.w / parseFloat(chip.style.width),
    opacity: 0,
    rotation: 0,
  })

  // Scene 2
  const phone2 = phone("phone-2.webp", 700, 121)
  const bubbleDebt = card("bubble-debt.webp", 101, 121, 336, 108)
  const bubbleSavings = card("bubble-savings.webp", 278, 259, 339, 108)
  const prefs = card("prefs.webp", 101, 523, 423, 436)

  // Scenes 3 to 5: the phone shell, with a masked scrolling screen and the
  // ask-us banner re-laid on top so its fade stays fixed while content moves.
  const shell = phone("phone-3.webp", 447, 135, "hm-shell")
  const screen = document.createElement("div")
  screen.className = "hm-screen"
  Object.assign(screen.style, { left: SCREEN.x + "px", top: SCREEN.y + "px", width: SCREEN.w + "px", height: SCREEN.h + "px" })
  const strip = document.createElement("img")
  strip.src = `${base}/dash-strip.webp`
  strip.alt = ""
  strip.loading = "lazy"
  strip.decoding = "async"
  Object.assign(strip.style, { width: SCREEN.w + "px", height: STRIP_H + "px" })
  screen.appendChild(strip)
  shell._float.appendChild(screen)
  const banner = document.createElement("img")
  banner.className = "hm-chrome"
  banner.src = `${base}/ask-banner.webp`
  banner.alt = ""
  banner.loading = "lazy"
  banner.decoding = "async"
  Object.assign(banner.style, { left: ASK_BANNER.x + "px", top: ASK_BANNER.y + "px", width: ASK_BANNER.w + "px", height: ASK_BANNER.h + "px" })
  shell._float.appendChild(banner)

  const goals = card("goals-menu.webp", 733, 347, 361, 376)
  const goalPress = pressOverlay(goals, GOAL_ROW)

  // Scene 6
  const phone6 = phone("phone-6.webp", 447, 135)

  // Off-stage positions. Phones alternate sides like the SimpleDocs windows.
  const offLeft = (l) => -(parseFloat(l.style.left) + PHONE.w + rnd(90, 320))
  const offRight = (l) => STAGE_W - parseFloat(l.style.left) + rnd(90, 320)
  const phones = [phone1, phone2, shell, phone6]
  phones.forEach((p, i) => {
    const enterLeft = i % 2 === 0
    p.dataset.enterx = enterLeft ? offLeft(p) : offRight(p)
    p.dataset.exitx = enterLeft ? offRight(p) : offLeft(p)
  })
  // Cards that fly in from the sides. Chips are handled separately: they grow
  // out of the ask field instead.
  const sideCards = [bubbleDebt, bubbleSavings, prefs, goals]
  sideCards.forEach((c) => {
    const side = (parseFloat(c.style.left) + parseFloat(c.style.width) / 2) > STAGE_W / 2 ? 1 : -1
    c.dataset.enterx = side * OFF; c.dataset.entery = rnd(-40, 40); c.dataset.enterrot = side * rnd(6, 11)
    c.dataset.exitx = side * OFF; c.dataset.exity = rnd(-30, 30); c.dataset.exitrot = side * rnd(8, 14)
  })
  const fx = (n) => (i, t) => +t.dataset[n]

  phones.forEach((p) => gsap.set(p, { x: +p.dataset.enterx }))
  sideCards.forEach((c) => gsap.set(c, { x: +c.dataset.enterx, y: +c.dataset.entery, rotation: +c.dataset.enterrot }))
  chips.forEach((c) => gsap.set(c, { transformOrigin: "0% 0%", ...tucked(c) }))

  const idle = [...chips, ...sideCards].map((c) => gsap.to(c._float, {
    y: rnd(6, 14) * (Math.random() < 0.5 ? -1 : 1),
    rotation: rnd(0.6, 1.8) * (Math.random() < 0.5 ? -1 : 1),
    duration: rnd(2.2, 3.4), yoyo: true, repeat: -1, ease: "sine.inOut", delay: rnd(0, 1.5),
  }))

  const tl = gsap.timeline({ repeat: -1 })
  const enterPhone = (p, position) =>
    tl.fromTo(p, { x: +p.dataset.enterx, y: 0 }, { x: 0, duration: 1.25, ease: "power2.out" }, position)
  const exitPhone = (p) =>
    tl.to(p, { x: +p.dataset.exitx, duration: 1.05, ease: "power2.in" })
  const enterCards = (list, position = "<+0.2") =>
    tl.fromTo(list,
      { x: fx("enterx"), y: fx("entery"), rotation: fx("enterrot"), scale: 1, opacity: 1 },
      { x: 0, y: 0, rotation: 0, duration: 0.95, ease: "power3.out", stagger: { each: 0.09, from: "random" } },
      position)
  const exitCards = (list, position = "<") =>
    tl.to(list,
      { x: fx("exitx"), y: fx("exity"), rotation: fx("exitrot"), duration: 0.75, ease: "power3.in", stagger: { each: 0.06, from: "random" } },
      position)
  // Squeeze the card and show the tint, then release.
  const press = (layer, overlay) => {
    tl.to(layer._float, { scale: 0.96, duration: 0.16, ease: "power2.out" })
    tl.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.16 }, "<")
    tl.to(layer._float, { scale: 1, duration: 0.3, ease: "power2.out" }, ">+0.08")
    tl.to(overlay, { opacity: 0, duration: 0.4 }, "<")
  }
  const hold = (s) => tl.to({}, { duration: s })

  // 1. Dashboard; the question chips grow out of the ask field, the third is picked.
  tl.addLabel("scene1")
  enterPhone(phone1, 0)
  chips.forEach((chip, i) => {
    tl.fromTo(chip, tucked(chip),
      { x: 0, y: 0, scale: 1, opacity: 1, rotation: 0, duration: 0.9, ease: "power3.out" },
      i === 0 ? "<+1.1" : "<+0.14")
  })
  hold(2.2)
  tl.addLabel("press1")
  press(chipC, chipCPress)
  // The picked chip glides back into the ask field; the others fade away.
  tl.to(chipC, { ...tucked(chipC), duration: 0.8, ease: "power2.inOut" }, "<+0.1")
  tl.to([chipA, chipB], { opacity: 0, scale: 0.96, duration: 0.4, ease: "power2.in", stagger: 0.06 }, "<")
  hold(0.3)
  exitPhone(phone1)

  // 2. The chat answer, with check-in bubbles and preferences.
  tl.addLabel("scene2", "-=0.15")
  enterPhone(phone2, "scene2")
  enterCards([bubbleDebt, bubbleSavings, prefs])
  hold(4.4)
  exitPhone(phone2)
  exitCards([bubbleDebt, bubbleSavings, prefs], "<")

  // 3 -> 4. Dashboard; the screen scrolls down to the next steps.
  tl.addLabel("scene3", "-=0.15")
  enterPhone(shell, "scene3")
  tl.set(strip, { y: 0 }, "scene3")
  hold(1.2)
  tl.addLabel("scroll")
  tl.to(strip, { y: -SCROLL, duration: 1.3, ease: "power2.inOut" })
  tl.addLabel("scene4")
  hold(2.2)

  // 5. Shift left, pick a new goal.
  tl.addLabel("scene5")
  tl.to(shell, { x: 226 - 447, duration: 0.9, ease: "power2.inOut" })
  enterCards([goals], "<+0.25")
  hold(1.6)
  tl.addLabel("press5")
  press(goals, goalPress)
  hold(0.5)
  exitCards([goals], ">")
  tl.to(shell, { x: +shell.dataset.exitx, duration: 1.05, ease: "power2.in" }, "<+0.1")

  // 6. The new goal's dashboard, then around again.
  tl.addLabel("scene6", "-=0.15")
  enterPhone(phone6, "scene6")
  hold(4.3)
  exitPhone(phone6)

  return { tl, idle }
}

// The phones sit on the same light card and drifting grid as SimpleDocs.
// On phones, zoom in on the 1120x900 window that holds every scene's content
// (x 101..1144, y 121..973) so the mockups read at a useful size. The mobile
// aspect ratio in index.css must match this window.
export default {
  stage: { w: STAGE_W, h: STAGE_H },
  focus: { x: 90, y: 100, w: 1120, h: 900, media: "(width < 40rem)" },
  build,
}
